import { createAsset } from '$/api/canvasRquest';
import type { UploadedImage } from '@/hooks/useImageUpload';
import { getUrlSearchParams } from '$/utils';
import { FileTypeEnum, getVideoCoverUrl } from '$/utils/upload';
import {
  AssetToolType,
  type AssetCreateParams,
  type AssetSourceScene,
} from '$/api/server.types';
import {
  addTeamFlowAssetsToLibrary,
  type TeamFlowAssetInputTrackScene,
  type TeamFlowAssetResource,
} from '$/pages/RoboneoTeamFlow/utils/registerNodeAsset';
import {
  getUploadedFileName,
  getUploadedMediaType,
  getUploadedVideoDuration,
} from './mediaCheckAfterUpload';

export { AssetToolType };

export type AiToolAssetMaterialType = 'image' | 'video' | 'audio';

export interface AiToolAssetLibraryInput {
  coverUrl?: string;
  duration?: number;
  height?: number;
  materialType: AiToolAssetMaterialType;
  name?: string;
  taskId?: string;
  url?: string;
  width?: number;
}

export interface AiToolCreateAssetInput extends AiToolAssetLibraryInput {
  sourceScene: AssetSourceScene;
  sourceId?: string;
  thumbnailUrl?: string;
  toolType: AssetToolType;
  watermarkUrl?: string;
}

export type AiToolUploadAssetToolType = AssetToolType | ((context: {
  file?: File;
  index: number;
  materialType: Extract<AiToolAssetMaterialType, 'image' | 'video'>;
  uploaded: UploadedImage;
}) => AssetToolType);

const autoSavedAssetKeys = new Set<string>();

/** 去掉 URL 查询串后提取资源后缀，供资产库展示文件类型使用。 */
function inferAssetSuffixFromUrl(url?: string): string {
  const cleanUrl = (url || '').split(/[?#]/)[0] || '';
  const filename = cleanUrl.split('/').pop() || '';
  const suffix = filename.includes('.') ? filename.split('.').pop() : '';
  return (suffix || '').replace(/^\./, '').toLowerCase();
}

/** 按资源类型推导兜底文件后缀。 */
function getDefaultAssetSuffix(materialType: AiToolAssetMaterialType): string {
  if (materialType === 'video') return 'mp4';
  if (materialType === 'audio') return 'mp3';
  return 'png';
}

/** 按资源类型生成兜底文件名，避免手动添加资产时名称为空。 */
function getFallbackAssetName(materialType: AiToolAssetMaterialType, suffix: string): string {
  let ext = suffix;
  if (!ext) {
    ext = getDefaultAssetSuffix(materialType);
  }
  return `RoboNeo-${Date.now()}.${ext}`;
}

/** 从当前地址读取 room_id，单功能页面没有房间上下文时保持省略。 */
function getCurrentRoomId() {
  if (typeof window === 'undefined') return '';
  return getUrlSearchParams().get('room_id')?.trim() || '';
}

/** 生成自动入库去重键，避免同一素材被页面和批量入口重复登记。 */
function getAutoSavedAssetKey(input: AiToolCreateAssetInput) {
  return [
    input.sourceScene,
    input.toolType,
    input.materialType,
    input.taskId || input.sourceId || '',
    input.url || '',
  ].join('|');
}

/** 自动入库前登记去重键，避免并发场景下重复请求 createAsset。 */
function markAutoAssetSaving(input: AiToolCreateAssetInput) {
  const key = getAutoSavedAssetKey(input);
  if (autoSavedAssetKeys.has(key)) return false;
  autoSavedAssetKeys.add(key);
  return true;
}

/** 自动入库失败时回滚去重键，允许后续相同素材再次尝试。 */
function unmarkAutoAssetSaving(input: AiToolCreateAssetInput) {
  autoSavedAssetKeys.delete(getAutoSavedAssetKey(input));
}

/** 将单功能工具素材转换为资产库 createAsset 请求参数。 */
function buildAiToolCreateAssetParams(input: AiToolCreateAssetInput): AssetCreateParams | null {
  const url = input.url?.trim();
  if (!url) return null;
  const suffix = inferAssetSuffixFromUrl(url) || getDefaultAssetSuffix(input.materialType);
  const roomId = getCurrentRoomId();

  return {
    task_type: 'tool',
    tool_type: input.toolType,
    material_type: input.materialType,
    source_scene: input.sourceScene,
    source_id: input.sourceId || input.taskId,
    task_id: input.taskId,
    url,
    name: input.name?.trim() || getFallbackAssetName(input.materialType, suffix),
    thumbnail_url: input.thumbnailUrl || input.coverUrl,
    watermark_url: input.watermarkUrl,
    width: input.width,
    height: input.height,
    duration: input.duration,
    ...(roomId ? { room_id: roomId } : {}),
  };
}

/** 将单功能工具结果转换为 TeamFlow 资产库资源参数。 */
function buildAiToolAssetResource(input: AiToolAssetLibraryInput): TeamFlowAssetResource | null {
  const url = input.url?.trim();
  if (!url) return null;
  const suffix = inferAssetSuffixFromUrl(url) || getDefaultAssetSuffix(input.materialType);

  return {
    materialType: input.materialType,
    url,
    name: input.name?.trim() || getFallbackAssetName(input.materialType, suffix),
    suffix,
    width: input.width,
    height: input.height,
    duration: input.duration,
    coverUrl: input.coverUrl,
    sourceId: input.taskId,
    sourceScene: 'task_generate',
  };
}

/** 调用资产库 createAsset，按单功能工具的 task_type/tool_type 写入素材。 */
export async function createAiToolAsset(input: AiToolCreateAssetInput) {
  const params = buildAiToolCreateAssetParams(input);
  if (!params) return null;
  const { asset_id: assetId } = await createAsset(params);
  return assetId;
}

/** 自动保存单功能资产；失败只记录日志，避免影响上传或生成主流程。 */
async function autoSaveAiToolAsset(input: AiToolCreateAssetInput, failedLog: string) {
  if (!markAutoAssetSaving(input)) return null;
  try {
    return await createAiToolAsset(input);
  } catch (error) {
    unmarkAutoAssetSaving(input);
    console.warn(failedLog, error);
    return null;
  }
}

/** 自动保存单功能生成结果；失败只记录日志，避免影响生成主流程。 */
export async function autoSaveAiToolGeneratedAsset(input: AiToolCreateAssetInput) {
  return autoSaveAiToolAsset(input, '单功能生成结果自动入库失败:');
}

/** 自动保存单功能上传素材；失败只记录日志，避免影响上传主流程。 */
export async function autoSaveAiToolUploadedAssets(
  uploaded: UploadedImage[],
  files: File[],
  toolType: AiToolUploadAssetToolType,
) {
  const tasks = uploaded.map(async (item, index) => {
    const file = item.sourceFile ?? files[item.sourceIndex ?? index];
    const mediaType = getUploadedMediaType(item, files, index);
    const materialType = mediaType === FileTypeEnum.Video ? 'video' : 'image';
    const resolvedToolType = typeof toolType === 'function'
      ? toolType({
        file,
        index,
        materialType,
        uploaded: item,
      })
      : toolType;

    return autoSaveAiToolAsset({
      toolType: resolvedToolType,
      sourceScene: 'import',
      materialType,
      url: item.url,
      name: getUploadedFileName(item, files, index),
      duration: materialType === 'video' ? await getUploadedVideoDuration(item, files, index) : undefined,
      thumbnailUrl: materialType === 'video' ? getVideoCoverUrl(item.url) : undefined,
    }, '单功能上传素材自动入库失败:');
  });
  const results = await Promise.allSettled(tasks);
  return results
    .map((result) => (result.status === 'fulfilled' ? result.value : null))
    .filter((assetId): assetId is number => assetId != null);
}

/** 点击「添加到资产库」时批量登记当前结果资源，并透传所属单功能工具的埋点场景。 */
export async function addAiToolResultsToAssetLibrary(
  inputs: AiToolAssetLibraryInput[],
  trackScene: TeamFlowAssetInputTrackScene,
) {
  const resources = inputs
    .map(buildAiToolAssetResource)
    .filter((item): item is TeamFlowAssetResource => Boolean(item));

  if (!resources.length) return [];
  const assetIds = await addTeamFlowAssetsToLibrary({ resources, trackScene });
  return assetIds.filter((assetId): assetId is number => assetId != null);
}
